import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "../../components/ui/sheet";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  FileText, Upload, Eye, Download, Trash2,
  Loader2, X, Calendar,Building, FileSignature, FileCheck2, Plus,UserCheck
} from "lucide-react";
import { CustomerDocument} from "../../types/documentTypes";
// import { toast } from "../../components/ui/use-toast";
import { toast } from "react-toastify";
import apiService from "../../services/apiService";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { getLocalStorageItem } from "../../utils";

interface CustomerDocumentsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string | undefined;
  customerName: string | undefined;
  // onUpload: (documentType: CustomerDocumentType, displayName: string, file: File, signedDate?: string) => void;
  onUpload: (check:any) => void;

  onDelete: (documentId: string) => void;
}

interface ApiResponse<T> {
  documents: T;
  message?: string;
  status?: number;
  // financial?:FinancialTabProps
}

export default function CustomerDocumentsSheet({
  open,
  onOpenChange,
  customerId,
  customerName,
  onUpload,
  onDelete
}: CustomerDocumentsSheetProps) {
  const [uploading, setUploading] = useState(false);
  const mraFileInputRef = useRef<HTMLInputElement>(null);
  const tsFileInputRef = useRef<HTMLInputElement>(null);
  const elFileInputRef = useRef<HTMLInputElement>(null);
  const louFileInputRef = useRef<HTMLInputElement>(null);
  const [mraSignedDate, setMraSignedDate] = useState("");
  const [tsSignedDate, setTsSignedDate] = useState("");
  const [showMraUpload, setShowMraUpload] = useState(false);
  const [showTsUpload, setShowTsUpload] = useState(false);
  const [showElUpload, setShowElUpload] = useState(false);
  const [showLouUpload, setShowLouUpload] = useState(false);
  const [uploadingEL, setUploadingEL] = useState(false);
  const [mraDoc, setMraDoc] = useState<any[]>([]);
  const [termsheetDocs, setTermSheetDocs] = useState<any[]>([]);
  const [engagementLetterDocs, setEngagementDocs] = useState<any[]>([]);
  const [louDocs, setLouDocs] = useState<any[]>([]);
  const [deletePopup, setDeletePopup]= useState(false);
  const [docViewModel, setDocViewModel] = useState(false); 
  const [viewDocUrl, setViewDocUrl] = useState("");
  const [delete_doc_id, setDeletedocId] = useState<number>();
  const [deletePopDataText, setDeletePopDataText] = useState("");
  const [viewPopupName, setviewPopupName] = useState("");

  const get_OtherDocuments = async () => {
    try {
      const response = await apiService.get<ApiResponse<any>>(
        `/customers/documents/mra/${customerId}`
      );

      const documents = response?.documents ?? [];
      const docType = (doc: any) =>
        String(doc.doc_type ?? doc.document_type ?? "").toLowerCase();

      // -- MRA Docs filter
      const result = documents.filter((doc: any) => docType(doc) === "mra");

      // -- Term Sheet Docs filter
      const resultTermSheet = documents
        .filter((doc: any) => docType(doc) === "ts")
        .sort(
          (a: any, b: any) =>
            new Date(b.assigned_date).getTime() -
            new Date(a.assigned_date).getTime()
        );

      // -- Engagement Letter Docs filter
      const engagementLetterDocs = documents.filter(
        (doc: any) => docType(doc) === "el"
      );

      // -- Letter of Undertaking Docs filter
      const letterOfUndertakingDocs = documents.filter(
        (doc: any) => docType(doc) === "lou"
      );

      setTermSheetDocs(resultTermSheet);
      setEngagementDocs(engagementLetterDocs);
      setLouDocs(letterOfUndertakingDocs);
      setMraDoc(result);
      const docCheck = {
        mra : result.length > 0 ? true : false,
        termsheet:resultTermSheet.length > 0 ? true : false
      }
      onUpload(docCheck)
    } catch (error) {
      console.error("Error fetching MRA documents:", error);
    }
  };

  useEffect(() => {
    if (customerId) {
      get_OtherDocuments();
    }
  }, [customerId]);



  const handleMRAUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const Mrafile = e.target.files?.[0];

      // Validate file size (50MB limit)
      if (!Mrafile) return;
      if (Mrafile.size > 50 * 1024 * 1024) {
        alert('Image must be less than 50MB');
        return;
      }

      if (Mrafile.type !== "application/pdf") {
        alert("Only PDF files are allowed");
        return;
      }

      setUploading(true);

      const formData = new FormData();
      formData.append("file", Mrafile);
      formData.append("customerId", customerId || "");
      formData.append("assignedDate", mraSignedDate);

      const response: any = await apiService.post(
        "/customers/other_documents/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (event) => {
            if (event.total) {
              const percent = Math.round((event.loaded * 100) / event.total);
            }
          }
        }
      );
    toast.success("Document uploaded successfully");

     setUploading(false);
     setShowMraUpload(false);
     setMraSignedDate("");

     get_OtherDocuments();
    }, [mraSignedDate, customerId]
  );

  const closeMraPopup = () => {
     setUploading(false);
     setShowMraUpload(false);
     setMraSignedDate("");
  }

  const closeTermSheetPopup = () => {
     setUploading(false);
     setShowTsUpload(false);
     setTsSignedDate("");
  }

  const closeELPopup = () => {
     setUploading(false);
     setShowElUpload(false);
  }

  const handleTSUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // ✅ Validate 10MB limit per file
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} must be less than 10MB`);
          return;
        }

        // ✅ Validate PDF
        if (file.type !== "application/pdf") {
          toast.error(`${file.name} is not a PDF file`);
          continue;
        }

        const formData = new FormData();
        formData.append("files", file);
        formData.append("customerId", customerId || "");
        formData.append("assignedDate", tsSignedDate);
        await apiService.post(
          "/customers/other_documents/Ts/upload",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      toast.success("Documents uploaded successfully");

      setShowTsUpload(false);
      setTsSignedDate("");
      get_OtherDocuments();

      } catch (error) {
        toast.error("Upload failed");
      } finally {
        setUploading(false);
        e.target.value = ""; // ✅ allows re-uploading same files again
      }
    },
    [tsSignedDate, customerId]
  );

  const handleELUploadDev = useCallback(
    async (doc_type: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadingEL(true);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // ✅ Validate 10MB limit per file
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} must be less than 10MB`);
          return;
        }

        // ✅ Validate PDF
        if (file.type !== "application/pdf") {
          toast.error(`${file.name} is not a PDF file`);
          continue;
        }

        const formData = new FormData();
        formData.append("files", file);
        formData.append("customerId", customerId || "");
        formData.append("doc_type", doc_type);
        await apiService.post(
          "/customers/other_documents/multiple_upload",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      toast.success("Documents uploaded successfully");
      if (doc_type === "lou") setShowLouUpload(false);
      else setShowElUpload(false);
      get_OtherDocuments();

      } catch (error) {
        toast.error("Upload failed");
      } finally {
        setUploading(false);
        setUploadingEL(false);
        e.target.value = ""; // ✅ allows re-uploading same files again
      }
    },
    [customerId]
  );

  const handleDownload = (doc: CustomerDocument) => {
    window.open(doc.url, "_blank");
  };

  const handleDownloaddev = async (url: string, fileName: string) => {
  try {

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank"; // important
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Download error:", error);
  }
};



  const handleViewDoc = (url: string, name: string) => {
    setviewPopupName(name);
    setDocViewModel(true);
    setViewDocUrl(url)
  }
  const handleCloseDec = () => {
    setDocViewModel(false);
    setviewPopupName('');

  }

  const openorcloseDeletePopup = (condition:string, doc_id: number, textData:string) => {
    if(condition == 'open'){
    setDeletePopup(true);
    setDeletedocId(doc_id);
    setDeletePopDataText(textData);
    }
    else{
    setDeletePopup(false);
    setDeletedocId(0);
    setDeletePopDataText('');
    }
  }


  const confirmDelete = async () => {
  try {
    
    if( delete_doc_id && delete_doc_id != 0){

    const pathBoody = {
      delete_doc_id,
      admin_user_id: getLocalStorageItem("detail")?.userId
    } 

    const response = await apiService.patch(
      "/customers/other_documents/delete",
      pathBoody);

    toast.success("Document deleted successfully");
    get_OtherDocuments();
    }
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  




  return (
    <>

      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Customer Documents
            </SheetTitle>
            <SheetDescription>
              Manage documents for {customerName}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Primary Documents - SaaS Style CTA Cards */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Primary Documents
              </h3>

              <div className="grid grid-cols-2 gap-4">
                {/* MRA Card */}
                <div
                  className={`relative group cursor-pointer rounded-xl border-2 transition-all duration-300 overflow-hidden ${mraDoc.length >0
                      ? 'border-emerald-500/50 bg-gradient-to-br from-emerald-500/5 via-emerald-500/10 to-emerald-600/5 hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-500/10'
                      : 'border-red-500/50 bg-gradient-to-br from-red-500/5 via-red-500/10 to-red-600/5 hover:border-red-500 hover:shadow-lg hover:shadow-red-500/10'
                    }`}
                  onClick={() => mraDoc.length>=0 && setShowMraUpload(mraDoc.length > 0 ? false : true)}
                >
                  {/* Status Indicator Glow */}
                  <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-30 ${mraDoc.length >0 ? 'bg-emerald-500' : 'bg-red-500'
                    }`} />

                  <div className="relative p-5">
                    {/* Icon & Status Badge */}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-xl ${mraDoc.length >0
                          ? 'bg-emerald-500/20 text-emerald-600'
                          : 'bg-red-500/20 text-red-500'
                        }`}>
                        <FileSignature className="h-6 w-6" />
                      </div>
                      <Badge
                        variant={mraDoc.length >0 ? "success" : "destructive"}
                        className={`text-xs font-medium ${mraDoc.length >0
                            ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
                            : 'bg-red-500/10 text-red-500 border-red-500/30'
                          }`}
                      >
                        {mraDoc.length >0 ? 'Signed' : 'Pending'}
                      </Badge>
                    </div>

                    {/* Title */}
                    <h4 className="font-semibold text-base mb-1">MRA</h4>
                    <p className="text-xs text-muted-foreground mb-3">Master Rental Agreement</p>

                    {/* Content based on status */}
                    {mraDoc[0] ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>{mraDoc[0].signed_at}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <UserCheck className="h-3 w-3" />
                          <span>{mraDoc[0].created_by}</span>
                        </div>
                        <div className="flex items-center gap-1 pt-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 hover:bg-emerald-500/10"
                            onClick={() => handleViewDoc(mraDoc[0].url, 'MRA')}
                          >
                            <Eye className="h-3.5 w-3.5" />

                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 hover:bg-emerald-500/10"
                            onClick={() => { handleDownloaddev(mraDoc[0].url, "MRA.pdf")}}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:bg-destructive/10"
                            onClick={() => { openorcloseDeletePopup('open', mraDoc[0].id, 'MRA'); onDelete(mraDoc[0].id); }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-red-500">
                        <Upload className="h-3.5 w-3.5" />
                        <span>Click to upload</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Termsheet Card */}
                <div
                  className={`relative group cursor-pointer rounded-xl border-2 transition-all duration-300 overflow-hidden ${termsheetDocs.length > 0
                      ? 'border-emerald-500/50 bg-gradient-to-br from-emerald-500/5 via-emerald-500/10 to-emerald-600/5 hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-500/10'
                      : 'border-red-500/50 bg-gradient-to-br from-red-500/5 via-red-500/10 to-red-600/5 hover:border-red-500 hover:shadow-lg hover:shadow-red-500/10'
                    }`}
                  onClick={() => setShowTsUpload(true)}
                  >
                  {/* Status Indicator Glow */}
                  <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-30 ${termsheetDocs.length > 0 ? 'bg-emerald-500' : 'bg-red-500'
                    }`} />

                  <div className="relative p-5">
                    {/* Icon & Status Badge */}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-xl ${termsheetDocs.length > 0
                          ? 'bg-emerald-500/20 text-emerald-600'
                          : 'bg-red-500/20 text-red-500'
                        }`}>
                        <FileCheck2 className="h-6 w-6" />
                      </div>
                      <Badge
                        variant={termsheetDocs.length > 0 ? "success" : "destructive"}
                        className={`text-xs font-medium ${termsheetDocs.length > 0
                            ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
                            : 'bg-red-500/10 text-red-500 border-red-500/30'
                          }`}
                      >
                        {termsheetDocs.length > 0 ? `${termsheetDocs.length} Signed` : 'Pending'}
                      </Badge>
                    </div>

                    {/* Title */}
                    <h4 className="font-semibold text-base mb-1">Termsheet</h4>
                    <p className="text-xs text-muted-foreground mb-3">Signed Agreement Terms</p>

                    {/* Content based on status */}
                    {termsheetDocs.length > 0 ? (
                      <div className="flex items-center gap-2 text-xs text-emerald-600">
                        <Plus className="h-3.5 w-3.5" />
                        <span>Add more termsheets</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-red-500">
                        <Upload className="h-3.5 w-3.5" />
                        <span>Click to upload</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* MRA Upload Modal */}
              {showMraUpload && mraDoc.length>=0 && (
                <div className="border rounded-lg p-4 bg-muted/30 space-y-3 animate-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Upload MRA</h4>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => closeMraPopup()}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Signed Date</Label>
                    <Input
                      type="date"
                      value={mraSignedDate}
                      onChange={(e) => setMraSignedDate(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <input
                    type="file"
                    ref={mraFileInputRef}
                    className="sr-only"
                    accept=".pdf"
                    disabled={!mraSignedDate}
                    onChange={handleMRAUpload}
                  />
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={() => mraFileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Select File
                  </Button>
                </div>
              )}

              {/* Termsheet Upload Modal */}
              {showTsUpload && (
                <div className="border rounded-lg p-4 bg-muted/30 space-y-3 animate-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">
                      Upload Termsheet {termsheetDocs.length > 0 ? `#${termsheetDocs.length + 1}` : ''}
                    </h4>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => closeTermSheetPopup()}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Signed Date</Label>
                    <Input
                      type="date"
                      value={tsSignedDate}
                      onChange={(e) => setTsSignedDate(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <input
                    type="file"
                    ref={tsFileInputRef}
                    className="sr-only"
                    accept=".pdf"
                    multiple
                    disabled={!tsSignedDate}
                    onChange={handleTSUpload}
                  />
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={() => tsFileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Select File
                  </Button>
                </div>
              )}

              {/* List of Termsheets */}
              {termsheetDocs.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground">Uploaded Termsheets</h4>
                  {termsheetDocs.map((doc, index) => (
                    <div key={Math.random()} className="border rounded-lg p-3 bg-background hover:bg-muted/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-emerald-500/10 rounded-md">
                            <FileCheck2 className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div>
                            <h5 className="text-sm font-medium">{doc.displayName || `Termsheet ${index + 1}`}</h5>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              {doc.signed_at && (
                                <span className="flex items-center gap-1 text-emerald-600">
                                  <Calendar className="h-3 w-3" />
                                  Signed: {doc.signed_at}
                                </span>
                              )}
                              <span className="flex items-center gap-1 text-emerald-600">
                                  <UserCheck className="h-3 w-3" />
                                  {doc.created_by}
                                </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleViewDoc(doc.url, `Termsheet ${index + 1}`)}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDownload(doc)}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => {openorcloseDeletePopup('open', doc.id, `Termsheet ${index + 1}`); onDelete(doc.id);}}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Letter of Undertaking Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Letter of Undertaking
                </h3>
                <Badge variant="outline" className="text-xs">
                  {louDocs.length} uploaded
                </Badge>
              </div>

              <div
                className={`relative group cursor-pointer rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden
                ${louDocs.length > 0
                    ? 'border-primary/30 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 hover:border-primary hover:shadow-lg hover:shadow-primary/10'
                    : 'border-muted-foreground/30 bg-muted/20 hover:border-primary/50 hover:bg-primary/5'
                  }`}
                onClick={() => setShowLouUpload(true)}
              >
                <div className="p-5">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl ${louDocs.length > 0
                        ? 'bg-primary/20 text-primary'
                        : 'bg-muted text-muted-foreground'
                      }`}>
                      <FileSignature className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-base mb-0.5">Upload Letter of Undertaking</h4>
                      <p className="text-xs text-muted-foreground">
                        Select multiple files (PDF • Max 10MB each)
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[var(--color-primary-dark)]">
                      <Plus className="h-4 w-4" />
                      <span>Add Files</span>
                    </div>
                  </div>
                </div>
              </div>

              {showLouUpload && (
                <div className="border rounded-lg p-4 bg-muted/30 space-y-3 animate-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Upload Letter of Undertaking</h4>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowLouUpload(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    You can select multiple files at once. Supported formats: PDF
                  </p>
                  <input
                    type="file"
                    ref={louFileInputRef}
                    className="sr-only"
                    accept=".pdf"
                    multiple
                    onChange={(e) => handleELUploadDev('lou', e)}
                  />
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={() => louFileInputRef.current?.click()}
                    disabled={uploadingEL}
                  >
                    {uploadingEL ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Select Files
                      </>
                    )}
                  </Button>
                </div>
              )}

              {louDocs.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground">Uploaded Files</h4>
                  {louDocs.map((doc, index) => (
                    <div key={doc.id ?? index} className="border rounded-lg p-3 bg-background hover:bg-muted/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary/10 rounded-md">
                            <FileSignature className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <h5 className="text-sm font-medium">{`Letter of Undertaking ${index + 1}`}</h5>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <UserCheck className="h-3 w-3" />
                                {doc.created_by}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleViewDoc(doc.url, `Letter of Undertaking ${index + 1}`)}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDownload(doc)}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => {openorcloseDeletePopup('open', doc.id, `Letter of Undertaking ${index + 1}`); onDelete(doc.id);}}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Engagement Letters Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Engagement Letters
                </h3>
                <Badge variant="outline" className="text-xs">
                  {engagementLetterDocs.length} uploaded
                </Badge>
              </div>

              {/* Upload Card */}
              <div
                className={`relative group cursor-pointer rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden
                ${engagementLetterDocs.length > 0
                    ? 'border-primary/30 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 hover:border-primary hover:shadow-lg hover:shadow-primary/10'
                    : 'border-muted-foreground/30 bg-muted/20 hover:border-primary/50 hover:bg-primary/5'
                  }`}
                onClick={() => setShowElUpload(true)}
              >
                <div className="p-5">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl ${engagementLetterDocs.length > 0
                        ? 'bg-primary/20 text-primary'
                        : 'bg-muted text-muted-foreground'
                      }`}>
                      <FileText className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-base mb-0.5">Upload Engagement Letters</h4>
                      <p className="text-xs text-muted-foreground">
                        Select multiple files (PDF • Max 10MB each)
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[var(--color-primary-dark)]">
                      <Plus className="h-4 w-4" />
                      <span>Add Files</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Upload Modal */}
              {showElUpload && (
                <div className="border rounded-lg p-4 bg-muted/30 space-y-3 animate-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Upload Engagement Letters</h4>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowElUpload(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    You can select multiple files at once. Supported formats: PDF
                  </p>
                  <input
                    type="file"
                    ref={elFileInputRef}
                    className="sr-only"
                    accept=".pdf"
                    multiple
                    onChange={(e) => handleELUploadDev('el', e)}
                  />
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={() => elFileInputRef.current?.click()}
                    disabled={uploadingEL}
                  >
                    {uploadingEL ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Select Files
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* List of Engagement Letters */}
              {engagementLetterDocs.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground">Uploaded Files</h4>
                  {engagementLetterDocs.map((doc, index) => (
                    <div key={doc.id} className="border rounded-lg p-3 bg-background hover:bg-muted/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary/10 rounded-md">
                            <FileText className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <h5 className="text-sm font-medium">{`Engagement Letter ${index + 1}`}</h5>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <UserCheck className="h-3 w-3" />
                                {doc.created_by}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleViewDoc(doc.url, `Engagement Letter ${index + 1}`)}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDownload(doc)}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => {openorcloseDeletePopup('open', doc.id, `Engagement Letter ${index + 1}`); onDelete(doc.id);}}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {deletePopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
            <div className="bg-white p-6 rounded-xl shadow-lg max-w-sm w-full">
              <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete <strong>{deletePopDataText}</strong>?
              </p>

              <div className="flex justify-end gap-2">
                <button
                  className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-100"
                  onClick={() => openorcloseDeletePopup('close', 0,'')}
                >
                  Cancel
                </button>

                <button
                  className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700"
                  onClick={() => { openorcloseDeletePopup('close', 0,''); confirmDelete()
                    
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
          )}
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* View Document Dialog */}
      <Dialog open={docViewModel} onOpenChange={()=>{setDocViewModel(false)}} >
        <DialogContent className="sm:max-w-[90%] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{viewPopupName}</DialogTitle>
          </DialogHeader>
          <div className="py-4 h-[70vh] overflow-auto">
            {viewDocUrl && (
              <iframe
                src={viewDocUrl}
                width="100%"
                height="600px"
                className="w-full h-full border-0"
                title={viewPopupName}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleCloseDec()} >
              Close
            </Button>
            <Button
              onClick={() => {handleDownloaddev(viewDocUrl, '')
              }}
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}